import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  SelectChangeEvent,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useState } from "react";
import { BundlePlan, SlotingGenderPreference, SlotSize } from "../../enums/common";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { ShortTimeEnum } from "../../enums/ShortTimeEnum";
import EventBus from "../../EventBus";
import PartnerSlotService from "../../services/PartnerTimeSlotService";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import { PartnerTimeSlotTable } from "./PartnerTimeSlotTable";
import "./styles.css";
import ResourceSelectDialog from "../Common/ResourceSelectDialog";
import LocalityList from "../Locality/LocalityList";
import { setCustomSelectOpen } from "../../store/reducers/customSelect";
import { useAppDispatch } from "../../store/hooks";
import { DatePicker } from "@mui/x-date-pickers";
import CancelIcon from '@mui/icons-material/Cancel';
import TransactionButton from "../Common/TransactionButton";
import PartnerSlotLocalFilter from "./PartnerSlotLocalFilter";

//Common styles
import {
  FilterContainer,
  FilterInput,
  FilterSection,
  FilterTextField,
  FilterWrapper,
  FIlterHeader,
  SectionHeader,
  SectionTitle,
  WorkspaceContainer,
  WorkSpaceTitleRow,
} from "../../styles/commonStyles";
import { FilterIcon } from "../Svg";

const LEGENDS = [
  { color: "red", label: "Blocked" },
  { color: "yellow", label: "CK_Blocked" },
  { color: "green", label: "Free" },
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const PartnerSlotFilterForm = () => {
  const partnerSlotService: PartnerSlotService = new PartnerSlotService();
  const theme = useTheme();
  const [spinner, setSpinner] = useState(<p>Data not available</p>);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [data, setData] = useState<any[]>();
  const [locality, setLocality] = useState<any>();
  const [gender, setGender] = useState<string>("");
  const [slotSize, setSlotSize] = useState<number>(1.5);
  const [radius, setRadius] = useState<any>();
  const [category, setCategory] = useState<BundlePlan>(BundlePlan.POPULAR);
  const [visitOneTime, setVisitOneTime] = useState<ShortTimeEnum[]>([]);
  const [visitTwoTime, setVisitTwoTime] = useState<ShortTimeEnum[]>([]);
  const [dateUpdatedByAdmin, setDateUpdatedByAdmin] = useState<any>(null);
  const [dateUpdatedByCook, setDateUpdatedByCook] = useState<any>(null);
  const [originalData, setOriginalData] = useState();
  const dispatch = useAppDispatch();

  const handleOpenLocality = () => {
    dispatch(setCustomSelectOpen({open: true}));
  }

  const handleCloseLocality = (e: any) => {
    setLocality(e);
  };

  const handleGender = (e: any) => {
    setGender(e.target.value);
  };

  const handleRadius = (e: any) => {
    setRadius(e.target.value)
  }

  const handleSlotSize = (e: any) => {
    setSlotSize(e.target.value);
  };

  const handleCategory = (e: any) => {
    setCategory(e.target.value);
  };

  const handleVisitOneTime = (
    event: SelectChangeEvent<typeof visitOneTime>
  ) => {
    const {
      target: { value },
    } = event;
    setVisitOneTime(value as ShortTimeEnum[]);
  };

  const handleVisitOneTimeDelete = (e: any, value: any) => {
    e.preventDefault();
    setVisitOneTime(visitOneTime.filter((e) => e !== value))
  }

  const handleVisitTwoTimeDelete = (e: any, value: any) => {
    e.preventDefault();
    setVisitTwoTime(visitTwoTime.filter((e) => e !== value))
  }

  const handleVisitTwoTime = (
    event: SelectChangeEvent<typeof visitTwoTime>
  ) => {
    const {
      target: { value },
    } = event;
    setVisitTwoTime(value as ShortTimeEnum[]);
  };

  const handleSubmit = async (e: any) => {
    setSpinner(<Box sx={{ display: "flex" }}>
      <CircularProgress />
    </Box>);
    if (
      !locality
    ) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Data not valid",
        type: CustomSnackbarTypes.ERROR
      });
      return;
    }

    const response = await partnerSlotService.getFilteredCooks(
      locality?.id,
      radius,
      gender as string,
      slotSize,
      category,
      visitOneTime,
      visitTwoTime,
     dateUpdatedByAdmin,
     dateUpdatedByCook,
    );
    setData(response);
    setOriginalData(response);
    setIsDataFetched(true);
  };

  function getStyles(
    name: string,
    visitOneTime: readonly string[],
    theme: Theme
  ) {
    return {
      fontWeight:
        visitOneTime.indexOf(name) === -1
          ? theme.typography.fontWeightRegular
          : theme.typography.fontWeightMedium,
    };
  }

  const updateDataFromFilter = useCallback((filterData: any, noOfFilter: number) => {
    if(noOfFilter > 0) {
      setData(filterData)
    } else {
      setData(originalData)
    }
  }, [originalData])
  
  return (
    <WorkspaceContainer>
     <ResourceSelectDialog
        name={"Localities"}
        list={LocalityList}
        onClose={handleCloseLocality}
      />

      {/* ── Filters ── */}
      <FilterWrapper>
        <FIlterHeader>
          <FilterSection style={{ marginBottom: "0" }}>
            <SectionHeader>
              <WorkSpaceTitleRow>
                <FilterIcon color="#1976d2" height={20} width={20} />
                <SectionTitle>Filters</SectionTitle>
              </WorkSpaceTitleRow>
            </SectionHeader>
            <Button
              onClick={handleSubmit}
              variant="contained"
              size="small"
              type="submit"
              sx={{ textTransform: "none", fontWeight: 600, px: 2.5 }}
            >
              Get Cooks
            </Button>
          </FilterSection>
        </FIlterHeader>

        <FilterContainer style={{ flexWrap: "wrap", rowGap: "12px" }}>
            <TransactionButton
              name={locality ? `LocalityId: ${locality?.id}` : "Choose Locality"}
              onClickHandler={() => handleOpenLocality()}
            />

            <FilterInput
              value={radius ?? ""}
              type="number"
              size="small"
              variant="outlined"
              onChange={handleRadius}
              label="Radius In Meter"
              sx={{ minWidth: 150 }}
            />

            <FilterTextField
              select
              label="Slot Size"
              size="small"
              variant="outlined"
              value={slotSize}
              onChange={(e: any) => handleSlotSize(e)}
              sx={{ minWidth: 130 }}
            >
              {Object.values(SlotSize).map((val) => {
                return (
                  <MenuItem key={val} value={val}>
                    {val}
                  </MenuItem>
                );
              })}
            </FilterTextField>

            <FilterTextField
              select
              label="Gender"
              size="small"
              variant="outlined"
              value={gender}
              onChange={(e: any) => handleGender(e)}
              sx={{ minWidth: 130 }}
            >
              {Object.values(SlotingGenderPreference).map((val) => {
                return (
                  <MenuItem key={val} value={val}>
                    {val}
                  </MenuItem>
                );
              })}
            </FilterTextField>

            <FilterTextField
              select
              label="Category"
              size="small"
              variant="outlined"
              value={category}
              onChange={(e: any) => handleCategory(e)}
              sx={{ minWidth: 140 }}
            >
              {Object.values(BundlePlan).map((val) => {
                return (
                  <MenuItem key={val} value={val}>
                    {val}
                  </MenuItem>
                );
              })}
            </FilterTextField>

            <FilterTextField
              select
              label="Visit One Time"
              size="small"
              variant="outlined"
              value={visitOneTime || ""}
              onChange={(e: any) => handleVisitOneTime(e)}
              SelectProps={{
                multiple: true,
                autoWidth: true,
                MenuProps: MenuProps,
                renderValue: (selected: any) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(selected as ShortTimeEnum[]).map((value) => (
                      <Chip
                        key={value}
                        label={value}
                        size="small"
                        clickable
                        deleteIcon={
                          <CancelIcon
                            onMouseDown={(e) => {
                              e.stopPropagation();
                            }}
                          />
                        }
                        onDelete={(e) => {
                          handleVisitOneTimeDelete(e, value);
                        }}
                      />
                    ))}
                  </Box>
                ),
              }}
              sx={{ minWidth: 190 }}
            >
              {Object.values(ShortTimeEnum).map((name) => (
                <MenuItem
                  key={name}
                  value={name}
                  style={getStyles(name, visitOneTime, theme)}
                >
                  {name}
                </MenuItem>
              ))}
            </FilterTextField>

            <FilterTextField
              select
              label="Visit Two Time"
              size="small"
              variant="outlined"
              value={visitTwoTime || ""}
              onChange={(e: any) => handleVisitTwoTime(e)}
              SelectProps={{
                multiple: true,
                autoWidth: true,
                MenuProps: MenuProps,
                renderValue: (selected: any) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(selected as ShortTimeEnum[]).map((value) => (
                      <Chip key={value} label={value} size="small" clickable
                      deleteIcon={
                        <CancelIcon
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                        />
                      }
                      onDelete={(e) => {
                        handleVisitTwoTimeDelete(e, value);
                      }}/>
                    ))}
                  </Box>
                ),
              }}
              sx={{ minWidth: 190 }}
            >
              {Object.values(ShortTimeEnum).map((name) => (
                <MenuItem
                  key={name}
                  value={name}
                  style={getStyles(name, visitTwoTime, theme)}
                >
                  {name}
                </MenuItem>
              ))}
            </FilterTextField>

            <DatePicker
              label="Last Updated By Admin"
              inputFormat="dd MMM yyyy"
              value={dateUpdatedByAdmin}
              disableMaskedInput
              onChange={(newValue) => {
                setDateUpdatedByAdmin(newValue);
              }}
              renderInput={(params) => {
                return <FilterTextField {...params} size="small" variant="outlined" sx={{ minWidth: 190 }} />;
              }}
            />

            <DatePicker
              label="Last Updated By Cook"
              inputFormat="dd MMM yyyy"
              value={dateUpdatedByCook}
              disableMaskedInput
              onChange={(newValue) => {
                setDateUpdatedByCook(newValue);
              }}
              renderInput={(params) => {
                return <FilterTextField {...params} size="small" variant="outlined" sx={{ minWidth: 190 }} />;
              }}
            />
        </FilterContainer>
      </FilterWrapper>

      <br />

      { isDataFetched ?
        <PartnerSlotLocalFilter data={originalData} callback={(filterData, filters) => updateDataFromFilter(filterData, filters)}/>
        : <></>
      }
      {
      isDataFetched ? (
        <div className="partnerTimeSlotTable">
          <PartnerTimeSlotTable
            isFetched={isDataFetched}
            data={data}
          ></PartnerTimeSlotTable>
        </div>
      ) : (
        spinner
      )}

      {/* ── Legend ── */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          columnGap: "28px",
          rowGap: "10px",
          backgroundColor: "#fff",
          borderTop: "1px solid rgb(225, 231, 239)",
          padding: "14px 20px",
          marginTop: "16px",
        }}
      >
        {LEGENDS.map((legend) => (
          <Box key={legend.label} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: legend.color,
                borderRadius: "4px",
                border: "1px solid rgba(0, 0, 0, 0.1)",
                flexShrink: 0,
              }}
            />
            <Typography fontSize={12}>{legend.label}</Typography>
          </Box>
        ))}
      </Box>
    </WorkspaceContainer>
  );
};